import React from "react";
import { Chip, SxProps, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { IntlShape, useIntl } from "react-intl";

import { DimensionValue } from "../../../services/Category";
import { SELECT_ALL_NAME } from "../../../hooks/useSelectAllOptions";
import { GenericObject } from "../../../services/Price";

const useStyles = makeStyles()(() => ({
  root: {
    height: "unset",
    borderRadius: "6px",
    padding: 0,
    "& .MuiChip-deleteIcon": {
      width: "12px",
      fontSize: "16px",
    },
    "& span": {
      padding:
        "0 calc(var(--mui-spacing) * 1.5) 0 calc(var(--mui-spacing) * 1)",
    },
  },
}));

const getLabel = (
  option: any,
  labelKey: string | null,
  translateLabel: boolean,
  intl: IntlShape
) => {
  return translateLabel
    ? intl.formatMessage({
        id: option[labelKey as keyof DimensionValue],
      })
    : labelKey
    ? option[labelKey as keyof DimensionValue]
    : option;
};

// For the Autocomplete rendering ChipTag: keep the tags on one line. MUI adds
// right padding for the clear icon on hover/focus, which otherwise wraps the
// "+N" onto a second line; the chips shrink instead (their label ellipsizes).
export const SX_SINGLE_LINE_TAGS: SxProps = {
  "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap" },
  "& .MuiAutocomplete-tag": { minWidth: 0 },
};

// Returns an array, not a fragment: MUI only applies `limitTags` (truncate
// when blurred, expand on focus) to an array of tags.
export const ChipTag = ({
  getTagProps,
  labelKey = "label",
  maxVisibleTags,
  selectAllOptionId = null,
  translateLabel = false,
  value,
}: {
  getTagProps: Function;
  labelKey?: string | null;
  // Caps the chips even while focused. The Autocomplete must then get
  // `limitTags={-1}`, otherwise MUI truncates the capped list a second time.
  maxVisibleTags?: number;
  selectAllOptionId?: number | string | null;
  translateLabel?: boolean;
  value: GenericObject[];
}) => {
  const intl = useIntl();
  const { classes } = useStyles();
  // Keep each chip's index in `value`: getTagProps' delete removes by index,
  // so indices of a copy without "All" would delete the wrong option.
  const visible = value
    .map((option, index) => ({ option, index }))
    .filter(
      ({ option }) =>
        !(
          option?.name === SELECT_ALL_NAME ||
          option?.id === SELECT_ALL_NAME ||
          option?.id === selectAllOptionId
        )
    );
  // A negative cap means no cap, like MUI's `limitTags`
  const shown =
    maxVisibleTags === undefined || maxVisibleTags < 0
      ? visible
      : visible.slice(0, maxVisibleTags);
  const hidden = visible.length - shown.length;

  const chips: React.ReactElement[] = shown.map(({ option, index }) => {
    const { key, ...tagProps } = getTagProps({ index });
    return (
      <Chip
        {...tagProps}
        key={key ?? index}
        label={getLabel(option, labelKey, translateLabel, intl)}
        classes={{ root: classes.root }}
        color="secondary"
        deleteIcon={<BackspaceIcon />}
      />
    );
  });

  if (hidden > 0) {
    chips.push(
      <Typography component="span" variant="body2" key="hidden-tags">
        {` +${hidden}`}
      </Typography>
    );
  }

  return chips;
};
